import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  DollarSign,
  TrendingUp,
  Percent,
  Target,
  Gauge,
  ShoppingCart,
  Receipt,
  BarChart3,
} from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { AppLayout } from "@/components/layout/AppLayout";
import { KpiCard } from "@/components/dashboard/KpiCard";
import { PeriodSelector } from "@/components/dashboard/PeriodSelector";
import { SalespersonFilter } from "@/components/dashboard/SalespersonFilter";
import { EvolutionChart } from "@/components/dashboard/EvolutionChart";
import { RankingList } from "@/components/dashboard/RankingList";
import { SalespeopleTable } from "@/components/dashboard/SalespeopleTable";
import { Progress } from "@/components/ui/progress";
import { LoadingState, ErrorState } from "@/components/common/StateViews";
import { fmtBRL, fmtPct, fmtNum, PERIODS } from "@/lib/format";

const fetchDashboard = ({ period, salespersonId }) =>
  api
    .get("/dashboard", {
      params: {
        period,
        salesperson_id: salespersonId === "all" ? undefined : salespersonId,
      },
    })
    .then((r) => r.data);

export default function Dashboard() {
  const [period, setPeriod] = useState("month");
  const [salespersonId, setSalespersonId] = useState("all");

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["dashboard", period, salespersonId],
    queryFn: () => fetchDashboard({ period, salespersonId }),
    keepPreviousData: true,
  });

  const periodLabel = PERIODS.find((p) => p.value === period)?.label;

  return (
    <AppLayout>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">
            Dashboard
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Performance de vendas · {periodLabel}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <SalespersonFilter
            value={salespersonId}
            onChange={setSalespersonId}
            options={data?.salespeople_summary || []}
          />
          <PeriodSelector value={period} onChange={setPeriod} />
        </div>
      </div>

      {isLoading && <LoadingState label="Carregando dashboard..." />}
      {isError && (
        <ErrorState
          message={formatApiErrorDetail(error?.response?.data?.detail)}
          onRetry={refetch}
        />
      )}

      {data && !isError && (
        <div className={isFetching ? "opacity-70 transition-opacity" : "transition-opacity"}>
          {/* KPIs principais */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              testId="kpi-receita"
              title="Receita"
              value={fmtBRL(data.kpis.receita)}
              icon={DollarSign}
              delta={data.delta.receita}
              footer="vs. período anterior"
            />
            <KpiCard
              testId="kpi-lucro"
              title="Lucro"
              value={fmtBRL(data.kpis.lucro)}
              icon={TrendingUp}
              delta={data.delta.lucro}
              footer="vs. período anterior"
            />
            <KpiCard
              testId="kpi-margem"
              title="Margem"
              value={fmtPct(data.kpis.margem)}
              icon={Percent}
              delta={data.delta.margem}
              deltaSuffix="pp"
              footer="vs. período anterior"
            />
            <KpiCard
              testId="kpi-atingimento"
              title="Atingimento da meta"
              value={fmtPct(data.kpis.atingimento)}
              icon={Target}
            >
              <Progress value={Math.min(data.kpis.atingimento, 100)} className="h-2" />
              <p className="mt-2 font-num text-xs text-muted-foreground">
                Meta: {fmtBRL(data.kpis.meta)}
              </p>
            </KpiCard>
          </div>

          {/* KPIs secundários */}
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              testId="kpi-pace"
              title="Pace (projeção)"
              value={fmtBRL(data.kpis.pace)}
              icon={Gauge}
              footer={`${data.dias_decorridos}/${data.dias_totais} dias`}
            />
            <KpiCard
              testId="kpi-meta"
              title="Meta"
              value={fmtBRL(data.kpis.meta)}
              icon={Target}
            />
            <KpiCard
              testId="kpi-num-vendas"
              title="Nº de vendas"
              value={fmtNum(data.kpis.num_vendas)}
              icon={ShoppingCart}
            />
            <KpiCard
              testId="kpi-ticket-medio"
              title="Ticket médio"
              value={fmtBRL(data.kpis.ticket_medio)}
              icon={Receipt}
            />
          </div>

          {/* Gráfico + Ranking */}
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="rounded-md border border-border bg-card p-6 lg:col-span-2">
              <div className="mb-4 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-primary" />
                <h2 className="font-heading text-base font-semibold">
                  Evolução (últimos 6 meses)
                </h2>
              </div>
              <EvolutionChart data={data.evolution} />
            </div>

            <div className="rounded-md border border-border bg-card p-6">
              <h2 className="mb-4 font-heading text-base font-semibold">
                Top 5 vendedores
              </h2>
              <RankingList rows={data.ranking_top5} />
            </div>
          </div>

          {/* Resumo dos vendedores */}
          <div className="mt-6">
            <h2 className="mb-3 font-heading text-base font-semibold">
              Resumo dos vendedores
            </h2>
            <SalespeopleTable rows={data.salespeople_summary} />
          </div>
        </div>
      )}
    </AppLayout>
  );
}
